<template>
  <b-card :title="capitalize(roleType)">
    <!--suppress HtmlUnknownTag :: This is imported via mixin -->
    <pipeline-status-alert
      :pipeline-response-name="sectionResponseType"
      for-type="multiple" :for-role="roleType" :combined="true">
    </pipeline-status-alert>
    <b-form @submit.prevent="updateRoles">
      <!--suppress JSUnresolvedVariable :: personRoles Defined in groupRoles Mixin-->
      <!-- Remove Person Roles-->
      <remove-roles-form-group
        v-if="hasPersonRoles()"
        :roles="personRoles"
        :form-array.sync="formData.personRolesToRemove"
        :role-type="roleType" />
      <!--suppress JSUnresolvedVariable :: serviceManagers Defined in groupRoles Mixin-->
      <!-- Remove Service Managers-->
      <remove-roles-form-group
        v-if="hasServiceRoles()"
        :roles="serviceRoles"
        :form-array.sync="formData.serviceRolesToRemove"
        :role-type="roleType" />
      <!--suppress JSUnresolvedVariable :: created dynamically at creation -->
      <!-- Add Person Managers-->
      <add-roles-form-group :style="addPersonStyle()"
        v-if="hasPersonRoles()"
        :form-object.sync="formData.newPersonRoles"
        :role-kind="personRoleKind"
        :role-type="roleType" />
      <!--suppress JSUnresolvedVariable :: created dynamically at creation -->
      <!-- Add Service Managers-->
      <add-roles-form-group
        v-if="hasServiceRoles()"
        :form-object.sync="formData.newServiceRoles"
        :role-kind="serviceRoleKind"
        :role-type="roleType" />
      <!-- Submit -->
      <button :id="`btn-update-${roleType}`" class="btn btn-primary"
              :disabled="!canSubmit || formData.submitting === true">
        {{formData.submitting === true ? 'Submitting...' : `Update ${capitalize(roleType)}`}}
      </button>
      <template v-if="roleType === 'administrators' || roleType === 'contacts'">
        <b-alert class="mt-2"
          :show="formData.invalidState"
          :id="`collapse_edit-${roleType}-invalid-state`"
          variant="danger"
          dismissible
          @dismissed="formData.invalidState = false"
        >
          {{formData.invalidStateMessage}}
        </b-alert>
      </template>
    </b-form>
  </b-card>
</template>

<script>
import { auth, groupRoles, util } from '../../util/groups-mixin';
import AddRolesFormGroup from '../form/AddRolesFormGroup';
import RemoveRolesFormGroup from '../form/RemoveRolesFormGroup';
import PipelineStatusAlert from '../PipelineStatusAlert';

export default {
  name: 'EditRoleType',
  components: {
    AddRolesFormGroup,
    RemoveRolesFormGroup,
    PipelineStatusAlert,
  },
  mixins: [auth, groupRoles, util],
  props: {
    managedGroup: {
      type: Object,
      required: true,
    },
    roleType: {
      type: String,
      required: true,
    },
    roleKinds: {
      type: Array,
      required: true,
    },
    responseName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      uugid: this.managedGroup.uugid,
      sectionResponseType: this.responseName,
      serviceRoleKind: 'service',
      personRoleKind: 'person',
      groupRoleKind: 'group',
      formData: {
        submitting: false,
      },
    };
  },
  computed: {
    currentContactIds() {
      return this.personContacts.map(contact => contact.pid);
    },
    currentAdminIds() {
      return this.personAdministrators.map(admin => admin.pid);
    },
    personRoles() {
      if (this.roleType === 'managers') {
        return this.personManagers;
      }
      if (this.roleType === 'administrators') {
        return this.personAdministrators;
      }
      if (this.roleType === 'contacts') {
        return this.personContacts;
      }
      return [];
    },
    serviceRoles() {
      if (this.roleType === 'managers') {
        return this.serviceManagers;
      }
      if (this.roleType === 'administrators') {
        return this.serviceAdministrators;
      }
      if (this.roleType === 'viewers') {
        return this.serviceViewers;
      }
      return [];
    },
    canSubmit() {
      const vm = this;
      let hasRolesToRemove = false;
      let hasRolesToAdd = false;
      this.roleKinds.forEach((kind) => {
        if (!hasRolesToRemove) {
          if (vm.formData[`${kind.toLowerCase()}RolesToRemove`].length !== 0) {
            hasRolesToRemove = true;
          }
        }
        if (!hasRolesToAdd) {
          if (!vm.isEmpty(vm.formData[`new${vm.capitalize(kind)}Roles`].ids)) {
            hasRolesToAdd = true;
          }
        }
      });
      if (!hasRolesToAdd && !hasRolesToRemove) {
        return false;
      }
      let newRolesValid = true;
      this.roleKinds.forEach((kind) => {
        if (newRolesValid) {
          if (!vm.isEmpty(vm.formData[`new${vm.capitalize(kind)}Roles`].ids)
            && !vm.formData[`new${vm.capitalize(kind)}Roles`].isValid) {
            newRolesValid = false;
          }
        }
      });
      return newRolesValid;
    },
  },
  methods: {
    updateRoles() {
      const vm = this;
      vm.$store.dispatch('clearPipelinedStateResponse', this.responseName)
        .then(() => 'done');
      let validRemoval = true;
      if (vm.roleType === 'administrators' || vm.roleType === 'contacts') {
        const typeUpdating = vm.roleType === 'administrators' ? 'administrator' : 'contact';
        vm.formData.invalidState = false;
        // noinspection JSUnresolvedVariable
        validRemoval = vm.validRoleUpdate(
          vm.formData.personRolesToRemove, vm.formData.newPersonRoles.ids, typeUpdating,
        );
      }
      if (!validRemoval) {
        vm.formData.invalidState = true;
      } else if (vm.canSubmit) {
        this.formData.submitting = true;
        let personsToAdd = [];
        let servicesToAdd = [];
        let personsToRemove = [];
        let servicesToRemove = [];
        if (this.hasPersonRoles()) {
          personsToAdd = this.arrayFromDelimitedString(this.formData.newPersonRoles.ids);
          personsToRemove = this.formData.personRolesToRemove.map(role => role.roleId);
        }
        if (this.hasServiceRoles()) {
          servicesToAdd = this.arrayFromDelimitedString(this.formData.newServiceRoles.ids);
          servicesToRemove = this.formData.serviceRolesToRemove.map(role => role.roleId);
        }
        this.doModifyRole(personsToAdd, 'person', 'add')
          .then(() => this.doModifyRole(servicesToAdd, 'service', 'add'))
          .then(() => this.doModifyRole(personsToRemove, 'person', 'remove'))
          .then(() => this.doModifyRole(servicesToRemove, 'service', 'remove', false))
          .finally(() => {
            if (vm.roleType === 'administrators' || vm.roleType === 'contacts') {
              vm.formData.invalidStateMessage = '';
              vm.formData.invalidState = false;
            }
            vm.roleKinds.forEach((kind) => {
              vm.formData[`${kind.toLowerCase()}RolesToRemove`] = [];
              vm.formData[`new${vm.capitalize(kind)}Roles`].ids = '';
              vm.formData[`new${vm.capitalize(kind)}Roles`].isValid = true;
            });
            vm.formData.submitting = false;
            if (vm.roleType === 'administrators') {
              vm.redirectWhenNoLongerAdmin();
            }
          });
      }
    },
    /* Set Group Details as the selected tab if the authenticated removes her/himself as an Admin of this group */
    redirectWhenNoLongerAdmin() {
      if (!this.isAdminOfCurrentGroup) {
        this.$parent.setView('appGroupDetail');
      }
    },
    /* Create and return a promise for adding or removing a role from the current group. */
    doModifyRole(roleIds, roleKind, modify, retainResponse = true) {
      return this.$store.dispatch('batchModifyGroupRoles',
        {
          responseToSet: this.responseName,
          uugid: this.managedGroup.uugid,
          type: this.roleType,
          kind: roleKind,
          roleIds,
          modify,
          retainResponse,
        });
    },
    hasPersonRoles() {
      return this.roleKinds.includes(this.personRoleKind);
    },
    hasServiceRoles() {
      return this.roleKinds.includes(this.serviceRoleKind);
    },
    addPersonStyle() {
      if (this.hasPersonRoles() && this.roleKinds.length > 1) {
        return { 'margin-bottom': '0px' };
      }
      return '';
    },
    /**
     * Check to ensure that the action being taken on Administrators or Contacts will result in a valid state.
     * Primarily, if removing members from one of these roles, check to ensure the result will not remove all role
     * members as each groups is required to have at least 1 admin and 1 contact. Also, ensure that the resulting
     * operation does not leave only 1 person in the checked role that is also the only person in the dependent role.
     * Note: if a groups has only 1 admin and only 1 contact, they cannot be the same person.
     *
     * @param formRolesToRemove CAN BE EMPTY: Array of roles being removed, if any.
     * @param formRolesToAdd CAN BE EMPTY: Delimited string of roles being added, if any.
     * @param typeRoleUpdating Type being checked. Must be either 'contact' or 'administrator'
     * @returns {boolean} If the resulting operation will result in a valid state.
     */
    validRoleUpdate(formRolesToRemove, formRolesToAdd, typeRoleUpdating) {
      let valid = true;
      this.formData.invalidStateMessage = '';
      if (formRolesToRemove.length > 0) {
        const toAdd = this.arrayFromDelimitedString(formRolesToAdd);
        let currentRoleIds = [];
        if (typeRoleUpdating === 'contact') {
          currentRoleIds = this.currentContactIds;
        } else if (typeRoleUpdating === 'administrator') {
          currentRoleIds = this.currentAdminIds;
        }
        const dependentRoleIds = typeRoleUpdating === 'contact' ? this.currentAdminIds : this.currentContactIds;
        /* Make a copy of the currentRoleIds so we don't accidentally modify the app state */
        let copyCurrentRoleIds = currentRoleIds.slice(0);
        /* Mock what the resulting update will look like */
        copyCurrentRoleIds.push(...toAdd);
        /* Remove Duplicates */
        copyCurrentRoleIds = Array.from(new Set(copyCurrentRoleIds));
        const rolesToRemoveIds = [];
        formRolesToRemove.forEach(role => rolesToRemoveIds.push(role.roleId));
        copyCurrentRoleIds = copyCurrentRoleIds.filter(role => !rolesToRemoveIds.includes(role));
        /* Can't remove all administrators. */
        if (copyCurrentRoleIds.length === 0) {
          const roleType = typeRoleUpdating === 'contact' ? 'Contact' : 'Administrator';
          // noinspection JSUnusedGlobalSymbols
          this.formData.invalidStateMessage =
            `This operation would result in all ${roleType}s being removed, which is not allowed.
            Every group must have at least 1 ${roleType}.`;
          valid = false;
        }
        /* If result will leave a single Admin or Contact then check to ensure that person is not also the only
         * person in the dependent role. */
        if (copyCurrentRoleIds.length === 1 && dependentRoleIds.length === 1) {
          if (copyCurrentRoleIds[0] === dependentRoleIds[0]) {
            // noinspection JSUnusedGlobalSymbols
            this.formData.invalidStateMessage =
              'This operation would leave a single Administrator and single ' +
              'Contact who is the same person, which is not allowed.';
            valid = false;
          }
        }
      }
      return valid;
    },
  },
  created() {
    const vm = this;
    vm.roleKinds.forEach((kind) => {
      // Dynamically create the form data elements needed for this instantiation based on the roleKinds passed as prop
      const removeItemName = `${kind.toLowerCase()}RolesToRemove`;
      const addItemName = `new${vm.capitalize(kind)}Roles`;
      vm.$set(vm.formData, removeItemName, []);
      vm.$set(vm.formData, addItemName, { ids: '', isValid: true });
      // This property is only necessary for updating admins or contacts.
      if (vm.roleType === 'administrators' || vm.roleType === 'contacts') {
        vm.$set(vm.formData, 'invalidState', false);
        vm.$set(vm.formData, 'invalidStateMessage', '');
      }
    });
  },
};
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/edit-group/EditRoleType.vue